import { useEffect, useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react';
import { DateTime } from 'luxon';
import { Stack } from '@startuptools/ui/joy/Stack';
import { Typography } from '@startuptools/ui/joy/Typography';
import { FormLabel } from '@startuptools/ui/joy/FormLabel';
import { FormControl } from '@startuptools/ui/joy/FormControl';
import { FormHelperText } from '@startuptools/ui/joy/FormHelperText';
import { DateInput } from '@startuptools/ui/custom/DateInput';
import { Alert } from '@startuptools/ui/joy/Alert';
import { Box } from '@startuptools/ui/joy/Box';
import { Button } from '@startuptools/ui/joy/Button';
import { toast } from '@startuptools/ui/toast/use-toast';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { useWarrantProgramSuspenseQuery, WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import { useWpSetWiseRecordDayMutation, WiseDependencyBondsDocument } from '../../../../../graphql/react-operations';
import { captureException } from '@sentry/react';
import { ErrorDisplay } from '../../../../../components/error-display/ErrorDisplay';
import { useStepperStepContent } from '../../../../../components/steppers/common/StepperStepContentContext';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { GqlMeeting, GqlMeetingConcern, GqlWarrantProgram } from '../../../../../graphql/base-types.graphql';

type FormValues = {
  recordDay: DateTime;
};

export const StepWiseRecordDayNgWrap = ({ warrantProgramId }: { warrantProgramId: string }) => {
  const companyId = useCompanyId();
  const {
    data: { warrantProgram },
  } = useWarrantProgramSuspenseQuery({ variables: { companyId, warrantProgramId }, fetchPolicy: 'cache-and-network' });
  return <StepWiseRecordDay warrantProgram={warrantProgram} />;
};

export const StepWiseRecordDay = ({
  warrantProgram,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'wiseDepBondsRecordDay' | 'warrantStartDate'> & {
    meetingConcern?: (Pick<GqlMeetingConcern, 'id'> & { meeting: Pick<GqlMeeting, 'id' | 'startTime'> }) | null;
  };
}) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();

  const [doSubmit, { loading }] = useWpSetWiseRecordDayMutation({
    refetchQueries: [WiseDependencyBondsDocument, WarrantProgramDocument],
  });

  const defaultRecordDate = useMemo(() => {
    if (warrantProgram?.wiseDepBondsRecordDay) {
      return DateTime.fromISO(warrantProgram.wiseDepBondsRecordDay);
    }
    if (warrantProgram?.warrantStartDate) {
      const start = DateTime.fromISO(warrantProgram.warrantStartDate);
      if (start <= DateTime.local()) {
        return start;
      }
    }
    return DateTime.local().startOf('day');
  }, [warrantProgram]);

  const maxDate = useMemo(() => {
    const meeting = warrantProgram?.meetingConcern?.meeting;
    const warrantStartDate = warrantProgram?.warrantStartDate;

    if (!warrantStartDate) {
      return DateTime.local().startOf('day');
    }
    if (!meeting) {
      return DateTime.fromISO(warrantStartDate).startOf('day');
    }
    const meetingStartTime = DateTime.fromISO(meeting.startTime);
    return meetingStartTime < DateTime.fromISO(warrantStartDate)
      ? meetingStartTime
      : DateTime.fromISO(warrantStartDate).startOf('day');
  }, [warrantProgram]);

  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: { recordDay: defaultRecordDate },
  });

  const currentRecordDay = watch('recordDay');
  useEffect(() => {
    if (currentRecordDay && currentRecordDay.startOf('day') > maxDate.startOf('day')) {
      setValue('recordDay', currentRecordDay, { shouldValidate: true });
    }
  }, [maxDate, currentRecordDay, setValue]);

  const onSubmit: SubmitHandler<FormValues> = async values => {
    const recordDayUTC = values.recordDay.toUTC(0, { keepLocalTime: true });
    try {
      await doSubmit({
        variables: {
          companyId,
          input: {
            id: warrantProgram.id,
            wiseDepBondsRecordDay: recordDayUTC.toISO() ?? '',
          },
        },
      });
      await stepNext();
    } catch (error) {
      captureException(error);
      toast({
        title: <Trans id="Could not save" />,
        severity: 'danger',
        description: (
          <Trans id="Something wen wrong when saving, please try again and contact support if problem persists" />
        ),
      });
    }
  };

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Trans id="Record day" />
      </Typography>
      <Alert color="neutral">
        <Trans id="The record date is the date used to determine the number of shares in the company, which in turn serves as the basis for the valuation of the convertible." />
      </Alert>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="row" gap={4}>
          <Stack gap={2} sx={{ minWidth: 340, maxWidth: 340 }}>
            <Controller
              control={control}
              name="recordDay"
              rules={{
                required: true,
                validate: value => {
                  const dateValue = value.startOf('day');
                  return (dateValue && dateValue <= maxDate) || i18n._('Cannot be after the program start');
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl error={!!error}>
                  <FormLabel>
                    <Trans id="Record day" />
                  </FormLabel>
                  <DateInput
                    {...field}
                    value={field.value}
                    onChange={newValue => {
                      field.onChange(newValue);
                      if (newValue && error) {
                        setValue('recordDay', newValue, { shouldValidate: true });
                      }
                    }}
                    maxDate={maxDate}
                  />
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />

            {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

            <Box>
              <Button type="submit" loading={loading}>
                <Trans id="Save and Continue" />
              </Button>
            </Box>
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
};
